import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useHistoryStore, type ShiftRow } from '@/stores/history';
import { fetchShiftReportUrl } from '@/services/api/shift-url';
import { deleteShiftRemote } from '@/services/api/delete-shift';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatHumanDate } from '@/lib/date';
import { IconTrash, IconRefresh } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';

export function HistoryPage() {
  const profile = useAuthStore((s) => s.profile);
  const rows = useHistoryStore((s) => s.rows);
  const isLoading = useHistoryStore((s) => s.isLoading);
  const refresh = useHistoryStore((s) => s.refresh);
  const removeLocal = useHistoryStore((s) => s.removeLocal);
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    void refresh(profile.uid);
  }, [profile, refresh]);

  if (!profile) return null;

  async function onOpen(row: ShiftRow) {
    if (row.isPending || openingId) return;
    setError(null);
    setOpeningId(row.shiftId);
    // Open the tab synchronously inside the click gesture. iOS Safari and
    // standalone PWAs silently block window.open() once an await has elapsed
    // and the user-gesture token has expired.
    const tab = window.open('', '_blank');
    try {
      const url = await fetchShiftReportUrl(row.shiftId);
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (e) {
      if (tab && !tab.closed) tab.close();
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOpeningId(null);
    }
  }

  async function onDelete(row: ShiftRow) {
    const ok = await confirm({
      title: 'Supprimer le quart',
      message: `Supprimer le quart du ${formatHumanDate(row.dateStarted) || '—'} ?`,
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    setError(null);
    removeLocal(row.shiftId);
    try {
      await deleteShiftRemote(row.shiftId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      void refresh(profile!.uid);
    }
  }

  return (
    <div className="p-6 flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Historique</h1>
        <button
          onClick={() => void refresh(profile.uid)}
          className="text-white/70 hover:text-white p-1"
          aria-label="Rafraîchir"
        >
          <IconRefresh size={20} />
        </button>
      </header>

      {error && <p className="text-sm text-red-300">{error}</p>}
      {isLoading && <p className="text-sm text-white/60">Chargement…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="text-sm text-white/60">Aucun quart soumis.</p>
      )}

      <div className="flex flex-col gap-2">
        {rows.map((row) => {
          const date = formatHumanDate(row.dateStarted) || '—';
          const loading = openingId === row.shiftId;
          const disabled = row.isPending || openingId !== null;
          return (
            <div
              key={row.shiftId}
              role="button"
              tabIndex={row.isPending ? -1 : 0}
              aria-disabled={disabled}
              onClick={() => void onOpen(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void onOpen(row);
                }
              }}
              className={`bg-brand-dark/40 rounded-2xl p-3 shadow-card flex items-center gap-3 transition-opacity ${
                row.isPending ? 'cursor-default' : 'cursor-pointer hover:bg-brand-dark/55'
              } ${loading ? 'opacity-70' : ''}`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void onDelete(row);
                }}
                className="text-red-300/80 hover:text-red-300 p-1"
                aria-label="Supprimer"
              >
                <IconTrash size={18} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{date}</p>
                {row.isPending && (
                  <p className="text-xs text-amber-300">Rapport en attente</p>
                )}
              </div>
              {row.isPending ? (
                <span className="text-xs text-white/60">…</span>
              ) : loading ? (
                <Spinner />
              ) : (
                <span className="rounded-pill bg-brand-orange text-white text-sm font-semibold px-4 py-2">
                  {row.amount} $
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
